import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {Dayjs} from 'dayjs';
import { IDatePickerConfig, ISelectionEvent, SingleCalendarValue } from 'ng2-date-picker';

dayjs.extend(customParseFormat);

@Component({
    selector: 'a-date-picker',
    templateUrl: './a-date-picker.component.html',
    styles: [':host {display: block}'],
    styleUrls: ['./a-date-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ADatePickerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class ADatePickerComponent implements ControlValueAccessor {

    public config: IDatePickerConfig = {
        openOnFocus: false,
        firstDayOfWeek: 'mo',
        unSelectOnClick: false,
        closeOnSelect: true,
        closeOnSelectDelay: 0,
        min: this.minDate,
        max: this.maxDate,
        format: 'DD/MM/YYYY'
    };
    @Input()
    public label: string;

    @Input()
    public mode: string = 'day';

    @Input()
    public required: boolean;

    @Input()
    public disabled: boolean = false;
    public realDate: Dayjs;
    @Input()
    placeholder: string = '';
    private onNgChange: (instant: string) => void;
    private onNgTouched: () => void;
    private _maxDate: string;
    private _minDate: string;
    private defaultPlaceholder: string;

    @Input()
    public get maxDate(): string {
        return this._maxDate;
    }

    public set maxDate(maxDate: string) {
        this._maxDate = maxDate;
        this.config = {
            ...this.config,
            max: maxDate
        };
    }

    @Input()
    public get minDate(): string {
        return this._minDate;
    }

    public set minDate(minDate: string) {
        this._minDate = minDate;
        this.config = {
            ...this.config,
            min: minDate
        };
    }

    public getPlaceholder(): string {
        return this.placeholder === undefined ? this.defaultPlaceholder : this.placeholder;
    }

    public onChange(event: ISelectionEvent): void {
        const date = this.computeDate(event.date);
        if (!areDateEquals(date, this.realDate)) {
            if (date === undefined) {
                this.onNgChange(undefined);
            } else {
                this.onNgChange(date.format('YYYY-MM-DD'));
            }
        } else if (typeof date === 'undefined' && typeof this.realDate === 'undefined') {
            this.onNgChange(undefined);
        }
    }

    private computeDate(date: SingleCalendarValue): Dayjs {
        if (dayjs.isDayjs(date)) {
            return date;
        } else {
            const formats = ['DD-MM-YYYY', 'DD/MM/YYYY'];
            for (const format of formats) {
                const parsed = dayjs(date, format, true);
                if (parsed.isValid()) {
                    return parsed;
                }
            }
            return undefined;
        }
    }

    public onOpen(): void {
        this.onNgTouched();
    }

    public writeValue(obj: string): void {
        if (obj !== null) {
            this.realDate = obj === undefined ? undefined : dayjs(obj);
        }
    }

    public registerOnChange(fn: any): void {
        this.onNgChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onNgTouched = fn;
    }
}

function areDateEquals(date1?: Dayjs, date2?: Dayjs): boolean {
    if (!date1) {
        return !date2;
    }
    if (!date2) {
        return !date1;
    }
    return date1.isSame(date2);
}
